import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Dumbbell } from 'lucide-react'

export default function PrivacyPolicyPage() {
  useEffect(() => {
    document.title = 'Política de Privacidad | fitbdy'
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      'Política de privacidad de fitbdy. Conocé cómo recopilamos, usamos y protegemos tu información personal en nuestra aplicación de fitness.'
    )
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="min-h-screen bg-[#0C0C1D] text-white">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#0C0C1D]/90 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
              <Dumbbell className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-base font-bold text-white tracking-tight">fitbdy</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12 md:py-20">
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white mb-4">
          Política de Privacidad
        </h1>
        <p className="text-sm text-gray-500 mb-12">
          Última actualización: 12 de agosto de 2026
        </p>

        <div className="space-y-10 text-gray-300 leading-relaxed">

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">1. Introducción</h2>
            <p>
              fitbdy ("nosotros", "nuestra", "nuestro") opera la aplicación móvil fitbdy y el sitio web
              fitbdy.com (en adelante, el "Servicio"). Esta política de privacidad describe cómo
              recopilamos, usamos y protegemos tu información personal cuando utilizás nuestro Servicio.
            </p>
            <p>
              Al usar el Servicio, aceptás la recopilación y uso de información de acuerdo con esta
              política. Si no estás de acuerdo con alguna parte, te pedimos que no utilices el Servicio.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">2. Información que recopilamos</h2>

            <h3 className="text-lg font-semibold text-gray-100 mt-4">2.1 Información proporcionada por el usuario</h3>
            <ul className="list-disc pl-6 space-y-1.5 text-gray-400">
              <li>Nombre de usuario y dirección de correo electrónico</li>
              <li>Contraseña (almacenada de forma cifrada, nunca en texto plano)</li>
              <li>Información de perfil: foto, biografía, datos de entrenamiento</li>
              <li>Rutinas de entrenamiento, ejercicios y registros de series</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-100 mt-4">2.2 Información recopilada automáticamente</h3>
            <ul className="list-disc pl-6 space-y-1.5 text-gray-400">
              <li>Información del dispositivo (modelo, sistema operativo, identificadores)</li>
              <li>Registros de actividad dentro de la aplicación</li>
              <li>Dirección IP y datos de conexión</li>
              <li>Estadísticas de uso (frecuencia de entrenamientos, ejercicios realizados)</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-100 mt-4">2.3 Datos de salud (solo con Apple Watch)</h3>
            <ul className="list-disc pl-6 space-y-1.5 text-gray-400">
              <li>
                Si entrenás con un Apple Watch y das permiso, guardamos el resumen de salud de cada sesión: calorías
                quemadas, frecuencia cardíaca promedio y máxima, y tiempo en cada zona de pulso. Es una categoría especial
                de datos y la tratamos aparte — el detalle está en la sección 8.
              </li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">3. Uso de la información</h2>
            <p>Utilizamos la información recopilada para:</p>
            <ul className="list-disc pl-6 space-y-1.5 text-gray-400">
              <li>Proveer, operar y mantener el Servicio</li>
              <li>Gestionar tus rutinas, entrenamientos y progreso personal</li>
              <li>Mostrar estadísticas, rachas y records personales</li>
              <li>Habilitar funcionalidades sociales (seguir usuarios, compartir rutinas)</li>
              <li>Enviar notificaciones relacionadas con el Servicio</li>
              <li>Mejorar y personalizar la experiencia del usuario</li>
              <li>Cumplir con obligaciones legales</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">4. Funcionalidades sociales</h2>
            <p>
              fitbdy incluye características sociales que permiten a los usuarios interactuar entre sí.
              Al respecto:
            </p>
            <ul className="list-disc pl-6 space-y-1.5 text-gray-400">
              <li>Tu nombre de usuario y foto de perfil pueden ser visibles para otros usuarios de la plataforma</li>
              <li>Las rutinas que hagas públicas podrán ser vistas y copiadas por otros usuarios</li>
              <li>Tus estadísticas de entrenamiento (racha, cantidad de rutinas) pueden aparecer en tu perfil público</li>
              <li>Podés controlar la visibilidad de tu perfil desde la configuración</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">5. Almacenamiento y seguridad</h2>
            <p>
              Implementamos medidas de seguridad técnicas y organizativas para proteger tu información:
            </p>
            <ul className="list-disc pl-6 space-y-1.5 text-gray-400">
              <li>Cifrado de contraseñas con algoritmos seguros (BCrypt)</li>
              <li>Autenticación mediante tokens JWT</li>
              <li>Comunicaciones cifradas mediante HTTPS/TLS</li>
              <li>Almacenamiento en servidores seguros en la región de Sudamérica</li>
              <li>Acceso restringido a datos personales</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">6. Compartir información</h2>
            <p>
              No vendemos, alquilamos ni compartimos tu información personal con terceros, excepto en
              las siguientes circunstancias:
            </p>
            <ul className="list-disc pl-6 space-y-1.5 text-gray-400">
              <li>
                <span className="text-gray-200 font-medium">Proveedores de servicio:</span> usamos infraestructura y
                servicios de terceros (Amazon Web Services) para alojar la app y, mediante su servicio de IA (Bedrock),
                procesar tus datos para las funciones de inteligencia artificial descritas en la sección "Inteligencia
                artificial". Estos proveedores actúan por nuestra cuenta y no usan tus datos para fines propios.
              </li>
              <li>
                <span className="text-gray-200 font-medium">Requerimientos legales:</span> Cuando sea requerido por
                ley o por una autoridad judicial competente.
              </li>
            </ul>
            <p>
              Nunca vendemos tus datos personales ni los compartimos con anunciantes.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">7. Datos de entrenamiento</h2>
            <p>
              Los registros de entrenamientos, rutinas, series, pesos y estadísticas son datos que vos
              generás y controlás. Estos datos:
            </p>
            <ul className="list-disc pl-6 space-y-1.5 text-gray-400">
              <li>Son de tu propiedad y los podés eliminar en cualquier momento</li>
              <li>Se almacenan de forma segura asociados a tu cuenta</li>
              <li>
                No los vendemos ni los compartimos con anunciantes. Solo se procesan con nuestro proveedor de IA
                (ver "Inteligencia artificial") cuando usás una función que lo requiere, y únicamente para darte ese
                resultado
              </li>
              <li>Pueden ser exportados a pedido</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">8. Integración con Apple HealthKit</h2>
            <p>
              fitbdy se integra con Apple HealthKit en iOS y watchOS con tres propósitos específicos y limitados:
            </p>
            <ul className="list-disc pl-6 space-y-1.5 text-gray-400">
              <li>
                <span className="text-gray-200 font-medium">Registro de entrenamientos:</span> al finalizar un entrenamiento
                en tu Apple Watch, fitbdy guarda el workout en la app Salud como actividad de tipo "Entrenamiento de fuerza
                tradicional". Esto permite que tus entrenamientos contribuyan a tus anillos de Actividad y métricas nativas
                de Apple Health.
              </li>
              <li>
                <span className="text-gray-200 font-medium">Lanzamiento automático de la app del Apple Watch:</span> cuando
                iniciás un entrenamiento desde el iPhone, fitbdy usa HealthKit para abrir automáticamente la app en tu
                Apple Watch sin acción manual adicional.
              </li>
              <li>
                <span className="text-gray-200 font-medium">Métricas de la sesión:</span> mientras entrenás, la app del
                Apple Watch lee tu frecuencia cardíaca y la energía activa que quemás, para mostrarte el esfuerzo en vivo
                en el reloj y calcular el resumen del entrenamiento.
              </li>
            </ul>
            <p>
              <span className="text-gray-200 font-medium">Uso de datos de HealthKit:</span>
            </p>
            <ul className="list-disc pl-6 space-y-1.5 text-gray-400">
              <li>Los datos de HealthKit <span className="text-gray-200">no se comparten con terceros</span> bajo ninguna circunstancia.</li>
              <li>No se utilizan para publicidad, marketing, data mining ni análisis poblacional.</li>
              <li>
                <span className="text-gray-200 font-medium">Qué guardamos en nuestros servidores:</span> del entrenamiento
                medido por el reloj guardamos únicamente el <span className="text-gray-200">resumen</span> de la sesión —
                calorías totales, frecuencia cardíaca promedio, frecuencia cardíaca máxima y cuántos segundos pasaste en
                cada zona de pulso —, asociado a ese entrenamiento y a tu cuenta, para que puedas verlo en tu historial.
                No guardamos la serie de mediciones individuales de tu frecuencia cardíaca. El resto de lo que registramos
                (peso, repeticiones, series) proviene de lo que vos cargás en la app.
              </li>
              <li>
                Podés revocar el acceso de fitbdy a HealthKit en cualquier momento desde iPhone → Ajustes → Salud →
                Acceso y dispositivos → fitbdy. Si revocás la lectura de frecuencia cardíaca o de energía activa, la app
                sigue funcionando y tus entrenamientos se registran igual, pero sin pulso, calorías ni zonas. Si revocás
                todo el acceso, además dejamos de sincronizar workouts a Salud y de abrir automáticamente la app del
                Apple Watch.
              </li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">9. Inteligencia artificial</h2>
            <p>
              fitbdy usa inteligencia artificial para funciones como generar rutinas, analizar tu
              progreso y responder tus preguntas sobre tus entrenamientos. Para eso, procesamos tus
              datos con un proveedor de IA (Amazon Web Services — servicio Bedrock).
            </p>
            <ul className="list-disc pl-6 space-y-1.5 text-gray-400">
              <li>
                <span className="text-gray-200 font-medium">Qué se procesa:</span> según la función que
                uses, se envían tus datos de entrenamiento (rutinas, series, pesos, repeticiones,
                estadísticas) y, en la generación de rutina a partir de una foto, la imagen que subís.
              </li>
              <li>
                <span className="text-gray-200 font-medium">Solo tus datos:</span> el asistente accede
                únicamente a la información de tu propia cuenta, es de solo lectura y nunca a datos de
                otros usuarios.
              </li>
              <li>
                <span className="text-gray-200 font-medium">No se usa para entrenar modelos:</span> según
                los términos de nuestro proveedor de IA, tu contenido no se usa para mejorar ni entrenar
                sus modelos ni los de terceros, y no se comparte con los proveedores de los modelos.
              </li>
              <li>
                <span className="text-gray-200 font-medium">No es consejo médico:</span> las respuestas de
                la IA son orientativas y no sustituyen la indicación de un profesional de la salud o del
                deporte.
              </li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">10. Derechos del usuario</h2>
            <p>Tenés derecho a:</p>
            <ul className="list-disc pl-6 space-y-1.5 text-gray-400">
              <li>Acceder a tu información personal almacenada</li>
              <li>Solicitar la corrección de datos inexactos</li>
              <li>Solicitar la eliminación de tu cuenta y todos los datos asociados</li>
              <li>Exportar tus datos de entrenamiento</li>
              <li>Controlar la visibilidad de tu perfil e información pública</li>
            </ul>
            <p>
              Para ejercer cualquiera de estos derechos, contactanos en{' '}
              <a href="mailto:contacto@fitbdy.com" className="text-indigo-400 hover:text-indigo-300 hover:underline transition-colors">
                contacto@fitbdy.com
              </a>.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">11. Retención de datos</h2>
            <p>
              Conservamos tu información personal mientras tu cuenta esté activa o sea necesaria para
              proveer el Servicio. Si solicitás la eliminación de tu cuenta, eliminaremos tu información
              personal en un plazo de 30 días, salvo que exista una obligación legal de conservarla.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">12. Privacidad de menores</h2>
            <p>
              El Servicio no está dirigido a personas menores de 13 años. No recopilamos conscientemente
              información personal de menores de 13 años. Si sos padre/madre o tutor y sabés que tu hijo
              nos proporcionó información personal, contactanos para que podamos tomar las medidas
              necesarias.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">13. Cambios en esta política</h2>
            <p>
              Podemos actualizar esta política de privacidad periódicamente. Te notificaremos sobre
              cambios significativos a través del Servicio o por email. Te recomendamos revisar esta
              página periódicamente para estar informado de cualquier cambio.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">14. Contacto</h2>
            <p>
              Si tenés preguntas sobre esta política de privacidad o sobre cómo manejamos tu
              información, podés contactarnos en:
            </p>
            <ul className="list-none pl-0 space-y-1.5 mt-2 text-gray-400">
              <li>
                <span className="text-gray-200 font-medium">Email:</span>{' '}
                <a href="mailto:contacto@fitbdy.com" className="text-indigo-400 hover:text-indigo-300 hover:underline transition-colors">
                  contacto@fitbdy.com
                </a>
              </li>
              <li>
                <span className="text-gray-200 font-medium">Sitio web:</span> fitbdy.com
              </li>
            </ul>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 mt-8">
        <div className="max-w-4xl mx-auto px-6 text-center text-sm text-gray-600">
          &copy; {new Date().getFullYear()} fitbdy. Todos los derechos reservados.
        </div>
      </footer>
    </div>
  )
}
